import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  callCount,
  cameBackText,
  checkerSees,
  CHOICE_REFINEMENT,
  choiceLabels,
  choiceReplies,
  CHOICES,
  choiceSummary,
  choiceTransition,
  draftLabels,
  DRAFTS,
  initialChoice,
  initialPlan,
  initialReact,
  initialReflect,
  listTasksPlanned,
  loopLabels,
  loopRuns,
  LOOPS,
  PLAN_REFINEMENT,
  plannerTokensText,
  plannerViewLabels,
  PLANNER_VIEWS,
  planRuns,
  planSummary,
  planTransition,
  REACT_REFINEMENT,
  reactSummary,
  reactTransition,
  REFLECT_REFINEMENT,
  reflections,
  reflectSummary,
  reflectTokensText,
  reflectTransition,
  RELEASE_QUESTION,
  replyText,
  requestText,
  stepsPlannedText,
  thoughtText,
  toChoice,
  toDraft,
  tokensText,
  toLoop,
  toPlannerView,
  verdictText,
  type ChoiceAction,
  type PlanAction,
  type ReactAction,
  type ReflectAction,
} from './pattern-models';

import './relationship-labs.css';
import './pattern-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function PatternReactLab() {
  const [state, setState] = useState(initialReact);
  const act = (action: ReactAction) => setState((current) => reactTransition(current, action));
  const run = loopRuns[state.loop];
  const summary = reactSummary(state);
  return (
    <Lab
      title="What does a thought before each call show?"
      eyebrow="Patterns · the loop has a name"
      className="rel-lab pattern-lab"
    >
      <Predict>
        What does a thought before each call show? Choose a Loop, then press Run the question.
      </Predict>
      <div className="cb-controls">
        <label>
          Loop
          <ModelSelect
            aria-label="Loop"
            value={state.loop}
            onValueChange={(value) => act({ type: 'loop', value: toLoop(value) })}
            options={LOOPS.map((id) => ({ value: id, label: loopLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run the question
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{RELEASE_QUESTION}</pre>
      </Panel>
      <Table
        label="The steps"
        columns={['Step', 'Thought', 'Action']}
        template="minmax(0, 0.4fr) minmax(0, 1.8fr) minmax(0, 1.8fr)"
        rows={
          state.run
            ? run.steps.map((step, index) => [index + 1, thoughtText(step), step.action])
            : []
        }
      />
      {state.run ? (
        <Facts
          values={[
            ['Calls', String(callCount(run))],
            ['Prompt tokens', tokensText(run.promptTokens)],
            ['Task list read', 'no'],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{REACT_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function PatternPlanLab() {
  const [state, setState] = useState(initialPlan);
  const act = (action: PlanAction) => setState((current) => planTransition(current, action));
  const run = planRuns[state.view];
  const summary = planSummary(state);
  return (
    <Lab
      title="What does the planner write, and what does the loop do with it?"
      eyebrow="Patterns · plan first, then execute"
      className="rel-lab pattern-lab"
    >
      <Predict>
        What does the planner write, and what does the loop do with it? Choose what the Planner
        sees, then press Plan, then run.
      </Predict>
      <div className="cb-controls">
        <label>
          Planner sees
          <ModelSelect
            aria-label="Planner sees"
            value={state.view}
            onValueChange={(value) => act({ type: 'view', value: toPlannerView(value) })}
            options={PLANNER_VIEWS.map((id) => ({ value: id, label: plannerViewLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Plan, then run
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The planner was given">
        <pre aria-label="The planner was given">{run.sees}</pre>
      </Panel>
      <Table
        label="The plan"
        columns={['Step', 'The plan says', 'The loop calls']}
        template="minmax(0, 0.4fr) minmax(0, 2fr) minmax(0, 1.6fr)"
        rows={state.run ? run.steps.map((step, index) => [index + 1, step.plan, step.call]) : []}
      />
      <Panel label="What the loop did">
        <pre aria-label="What the loop did">{state.run ? run.loop : '(not run yet)'}</pre>
      </Panel>
      {state.run ? (
        <Facts
          values={[
            ['Planner prompt tokens', plannerTokensText(run)],
            ['Steps planned', stepsPlannedText(run)],
            ['list_tasks in the plan', listTasksPlanned(run)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{PLAN_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function PatternReflectLab() {
  const [state, setState] = useState(initialReflect);
  const act = (action: ReflectAction) => setState((current) => reflectTransition(current, action));
  const reflection = reflections[state.draft];
  const summary = reflectSummary(state);
  return (
    <Lab
      title="Does a reflection step catch the wrong answer?"
      eyebrow="Patterns · reflect before answering"
      className="rel-lab pattern-lab"
    >
      <Predict>
        Does a reflection step catch the wrong answer? Choose an Answer to check, then press
        Reflect.
      </Predict>
      <div className="cb-controls">
        <label>
          Answer to check
          <ModelSelect
            aria-label="Answer to check"
            value={state.draft}
            onValueChange={(value) => act({ type: 'draft', value: toDraft(value) })}
            options={DRAFTS.map((id) => ({ value: id, label: draftLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.taskList}
          onClick={() => act({ type: 'toggle-task-list' })}
        >
          Give the checker the task list
        </Button>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Reflect
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The draft">
        <pre aria-label="The draft">{`Q: ${reflection.question}\nA: ${reflection.draft}`}</pre>
      </Panel>
      <Table
        label="What the checker could see"
        columns={['What the checker could see']}
        rows={state.run ? checkerSees(state.draft, state.taskList).map((item) => [item]) : []}
      />
      {state.run ? (
        <Facts
          values={[
            ['Verdict', verdictText(state.draft, state.taskList)],
            ['Tokens', reflectTokensText(state.draft, state.taskList)],
            ['Checked for', 'contradiction'],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{REFLECT_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function PatternChoiceLab() {
  const [state, setState] = useState(initialChoice);
  const act = (action: ChoiceAction) => setState((current) => choiceTransition(current, action));
  const reply = choiceReplies[state.choice];
  const summary = choiceSummary(state);
  return (
    <Lab
      title="What does tool_choice do on this gateway?"
      eyebrow="Patterns · tell the model which tool, or none"
      className="rel-lab pattern-lab"
    >
      <Predict>
        What does tool_choice do on this gateway? Choose a tool_choice value, then press Send.
      </Predict>
      <div className="cb-controls">
        <label>
          tool_choice
          <ModelSelect
            aria-label="tool_choice"
            value={state.choice}
            onValueChange={(value) => act({ type: 'choice', value: toChoice(value) })}
            options={CHOICES.map((id) => ({ value: id, label: choiceLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.sent} onClick={() => act({ type: 'send' })}>
          Send
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The request">
        <pre aria-label="The request">{requestText(state.choice)}</pre>
      </Panel>
      <Panel label="The reply">
        <pre aria-label="The reply">{state.sent ? replyText(state.choice) : '(not sent yet)'}</pre>
      </Panel>
      {state.sent ? (
        <Facts
          values={[
            ['Finish reason', reply.finishReason],
            ['Came back', cameBackText(reply)],
            ['Setting', reply.honored],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.sent ? <p className="rel-caption">{CHOICE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
